import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DrawerContent } from "@/components/ui/drawer";
import { useFacilitiesContext } from "@/hooks/useFacilitiesContext";
import { useUnitItemContext } from "@/hooks/useUnitItemContext";
import { UnitPrice } from "@/components/units/unit/info/UnitPrice";
import { sanitizePhoneNumber, formatPhoneNumber } from "@/utils/phone/phoneUtils";
import type { Facility, UnitRates, UnitFeature } from "@/types";

const UnitInformationCard = ({
    width,
    length,
    area,
    features,
    rates,
    selectedPromoName,
}: {
    width: number;
    length: number;
    area: number;
    features: UnitFeature[];
    rates: UnitRates;
    selectedPromoName?: string;
}) => {
    return (
        <Card>
            <CardHeader>
                <CardTitle>Unit Information</CardTitle>
            </CardHeader>
            <CardContent>
                <dl>
                    <dt>Unit Size:</dt>
                    <dd>
                        {length}' x {width}'
                    </dd>
                </dl>
                <dl>
                    <dt>Sq. Ft.:</dt>
                    <dd>{area}</dd>
                </dl>
                <dl>
                    <dt>Features:</dt>
                    <dd>
                        {features.map((feature) => feature.name).join(", ")}
                    </dd>
                </dl>
                <UnitPrice rates={rates} />
                {selectedPromoName && (
                    <dl>
                        <dt>Special:</dt>
                        <dd>{selectedPromoName}</dd>
                    </dl>
                )}
            </CardContent>
        </Card>
    );
};

const FacilityInformationCard = ({ facility }: { facility: Facility }) => {
    const { name, address_1, city, state, zipcode, phone } = facility;

    return (
        <Card>
            <CardHeader>
                <CardTitle>{name}</CardTitle>
            </CardHeader>
            <CardContent>
                <address>
                    {address_1}
                    <br />
                    {city}, {state} {zipcode}
                    <br />
                    {phone && (
                        <span className="facility-phone">
                            Phone:{" "}
                            <strong>
                                <a href={`tel:${sanitizePhoneNumber(phone)}`}>
                                    {formatPhoneNumber(phone)}
                                </a>
                            </strong>
                        </span>
                    )}
                </address>
            </CardContent>
        </Card>
    );
};

export const JourneyFooter = () => {
    const { facilities } = useFacilitiesContext();
    const { unitGroup, selectedPromo } = useUnitItemContext();

    const currentFacility = facilities.find(
        (facility) => facility.id === unitGroup.representative_unit.facility_id,
    );

    if (!currentFacility) {
        return (
            <DrawerContent>
                <div className="tw:p-4">Loading facility information...</div>
            </DrawerContent>
        );
    }

    const { width, length, area, features } = unitGroup.representative_unit;
    const selectedPromoObject = unitGroup.rates.promo_rates?.find(
        (promo) => promo.id === selectedPromo,
    );

    return (
        <DrawerContent>
            <div className="tw:p-4">
                <div className="tw:flex tw:items-center tw:justify-center tw:space-x-2">
                    <UnitInformationCard
                        width={width}
                        length={length}
                        area={area}
                        features={features}
                        rates={unitGroup.rates}
                        selectedPromoName={selectedPromoObject?.name}
                    />
                    <FacilityInformationCard facility={currentFacility} />
                </div>
            </div>
        </DrawerContent>
    );
};
